<template>
    <div
        class="global-search w-100 position-relative"
        :class="props.watchlistPk ? 'pa-md alt-style' : ''"
        v-click-outside="
            () => {
                if (props.enableClickOutside) {
                    showResults = false
                }
            }
        "
    >
        <AonInput
            ref="searchInput"
            v-model="searchTerm"
            search-input
            id="global-search"
            :placeholder="t(`search.placeholder.${context}`)"
            @clear="clearSearch"
            @click="onClick"
            @keyup="handleKeyup"
        />
        <div
            class="dropdown-results bg-background-default w-100 position-absolute"
            :class="[
                { 'elevation-5': !props.watchlistPk },
                { 'alt-style': props.watchlistPk },
                { 'add-border': companyResults.length > 0 || loadingResults },
            ]"
        >
            <AonSpinner v-if="loadingResults" class="m-auto" :scale="0.5" />
            <!-- // TODO DRY up these two very similar context results blocks -->
            <template v-if="resultsVisible">
                <div class="company-results">
                    <div
                        v-if="!props.watchlistPk"
                        class="totals display-flex justify-between px-sm py-sm"
                    >
                        <div class="wrap display-flex large bold text-secondary">
                            <font-awesome-icon :icon="`fas fa-building`" />
                            <p class="text-secondary bold ml-sm">
                                {{ t('search.companies') }}
                                <span class="ml-xs" v-if="!loadingCompanyResults">{{
                                    companyResultsTotal
                                }}</span>
                            </p>
                        </div>
                        <p
                            v-if="!loadingCompanyResults"
                            class="link bold cursor-pointer"
                            data-cy="search-all-companies"
                            @click="searchAllPage('companies')"
                        >
                            {{ t('search.search.companies') }}
                        </p>
                        <AonSpinner v-if="loadingCompanyResults" class="m-auto" :scale="0.5" />
                    </div>
                    <div class="results" v-if="!loadingCompanyResults">
                        <p v-if="!companyResults.length" class="text-center pb-md">
                            {{ t('search.noResults') }}
                        </p>
                        <template v-else>
                            <template
                                v-for="(result, index) in companyResults"
                                :key="`highlight_company_${index}`"
                            >
                                <!-- Add divider before the first item with patent_count_us = 0 -->
                                <template
                                    v-if="
                                        result.patent_count_us === 0 &&
                                        ((index > 0 &&
                                            companyResults[index - 1].patent_count_us > 0) ||
                                            index === 0)
                                    "
                                >
                                    <hr class="my-sm" />
                                    <p class="text-secondary italic px-sm mb-sm">
                                        Results with no US patents
                                    </p>
                                </template>
                                <Highlighter
                                    class="result"
                                    :targetWords="result.name"
                                    :searchTerm="searchTerm"
                                    :ticker="result.stock_ticker"
                                    :company-pk="result.aon_entity_pk.toString()"
                                    :watchlistPk="props.watchlistPk"
                                    :company-params="result"
                                    @clear="clearSearch()"
                                />
                            </template>
                        </template>
                    </div>
                </div>

                <!-- Patent Results -->
                <div class="patent-results">
                    <div class="totals display-flex justify-between px-sm py-sm">
                        <div class="wrap display-flex large bold text-secondary">
                            <font-awesome-icon :icon="`fas fa-file-contract`" />
                            <p class="text-secondary bold ml-sm">
                                Patents
                                <span class="ml-xs" v-if="!loadingPatentResults">{{
                                    patentResultsTotal
                                }}</span>
                            </p>
                        </div>
                        <p
                            v-if="patentResults.length > 0 && !loadingPatentResults"
                            class="link bold cursor-pointer"
                            data-cy="search-all-patents"
                            @click="searchAllPatents()"
                        >
                            View All
                        </p>
                    </div>
                    <AonSpinner v-if="loadingPatentResults" class="m-auto" :scale="0.5" />
                    <div class="results" v-if="!loadingPatentResults">
                        <p v-if="!patentResults.length" class="text-center pb-md">
                            {{ t('search.noResults') }}
                        </p>
                        <template v-else>
                            <template
                                v-for="(result, index) in patentResults"
                                :key="`highlight_patent_${index}`"
                            >
                                <Highlighter
                                    class="result"
                                    :targetWords="result.patentId"
                                    :searchTerm="searchTerm"
                                    :country="result.country"
                                    :patent-id="result.publicationPk"
                                    @clear="clearSearch()"
                                />
                            </template>
                        </template>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash-es'
import { search } from '@/api/entities'
import { patentSearch } from '@/api/patents'

import Highlighter from './helpers/Highlighter'

const props = defineProps({
    context: {
        type: String,
        default: 'both',
    },
    ignoreEnter: {
        type: Boolean,
        default: false,
    },
    watchlistPk: {
        type: String,
        default: null,
    },
    enableClickOutside: {
        type: Boolean,
        default: false,
    },
})

const logger = inject('logger')
const eventBus = inject('eventBus')
const router = useRouter()
const { t } = useI18n()

const companyResults = ref([])
const companyResultsTotal = ref(0)
const patentResults = ref([])
const patentResultsTotal = ref(0)
const loadingResults = ref(false)
const loadingCompanyResults = ref(false)
const loadingPatentResults = ref(false)
const searchInput = ref('searchInput')
const searchTerm = ref('')
const showResults = ref(false)

const isSearchTermEmpty = computed(() => {
    return !searchTermTrimmed.value
})
const resultsVisible = computed(() => {
    return !loadingResults.value && showResults.value
})
const searchTermTrimmed = computed(() => {
    return searchTerm.value ? searchTerm.value.trim() : searchTerm.value
})

onMounted(async () => {
    await nextTick()
    setFocus()
    eventBus.on('update-watchlist', () => {
        clearSearch()
    })
})

const clearSearch = () => {
    searchTerm.value = ''
    showResults.value = false
    setFocus()
    companyResults.value = []
    companyResultsTotal.value = 0
    patentResults.value = []
    patentResultsTotal.value = 0
    loadingResults.value = false
}
const getScreenerFilter = (context) => {
    if (!searchTermTrimmed.value.length) {
        return
    }

    const filterColumn = context === 'companies' ? 'name' : 'aon_sector_name'

    return btoa(
        JSON.stringify({
            filters: [
                {
                    filterColumn,
                    filterType: 'complex_text',
                    filterLabel: 'Name',
                    filterValue: searchTermTrimmed.value,
                },
            ],
        })
    )
}
const liveSearch = debounce(async () => {
    if (searchTermTrimmed.value.length < 2) {
        return
    }

    // TODO handle async API requests with a fetch API abort(if applicable to the POST search request) OR a cancellationToken(deprecated)
    if (isSearchTermEmpty.value) {
        clearSearch()
        return
    }

    loadingResults.value = true
    loadingCompanyResults.value = true
    loadingPatentResults.value = true

    // Run both searches concurrently and show results as soon as either completes
    const companiesPromise = searchCompanies()
    const patentsPromise = searchPatents()

    companiesPromise.then(() => {
        loadingCompanyResults.value = false
    })
    patentsPromise.then(() => {
        loadingPatentResults.value = false
    })

    // Show results when either search completes
    Promise.race([companiesPromise, patentsPromise]).then(() => {
        showResults.value = true
        loadingResults.value = false
    })
}, 750)

const handleKeyup = (event) => {
    if (event.key !== 'Enter') {
        liveSearch()
    } else {
        if (!props.ignoreEnter) {
            liveSearch.cancel()
        }

        searchAllPage(null)
    }
}

const onClick = () => {
    showResults.value = isSearchTermEmpty.value ? false : true
}

const searchCompanies = async () => {
    const params = {
        //FV: [searchTermTrimmed.value],
        page_size: 5,
    }

    try {
        const response = await search(searchTermTrimmed.value, params)
        if (!response) {
            // Request was cancelled, do nothing
            return
        }
        if (response.status !== 200) {
            companyResults.value = []
            companyResultsTotal.value = 0
            throw new Error(`Companies search failure for ${searchTermTrimmed.value}`)
        }
        companyResultsTotal.value = response.data.rowCount.toLocaleString()
        companyResults.value = response.data.results.sort((a, b) => {
            // If both have patents or both don't have patents, maintain original order
            if (
                (a.patent_count_us > 0 && b.patent_count_us > 0) ||
                (a.patent_count_us === 0 && b.patent_count_us === 0)
            ) {
                return 0
            }
            // Companies with patents come first
            return b.patent_count_us > 0 ? 1 : -1
        })
    } catch (err) {
        logger.error(err)
    }
}
const searchPatents = async () => {
    const params = {
        FV: [searchTermTrimmed.value],
        FT: ['contains'],
        FC: ['patentId'],
        page_size: 5,
    }

    try {
        const response = await patentSearch(params)
        if (!response) {
            // Request was cancelled, do nothing
            return
        }
        if (response.status !== 200) {
            patentResults.value = []
            patentResultsTotal.value = 0
            throw new Error(`Patents search failure for ${searchTermTrimmed.value}`)
        }
        patentResultsTotal.value = response.data.rowCount.toLocaleString()
        patentResults.value = response.data.results
    } catch (err) {
        logger.error(err)
    }
}
const searchAllPage = async (context) => {
    if (props.ignoreEnter && !context) {
        return
    }

    if (!searchTermTrimmed.value.length && !context) {
        return
    }

    const filter = getScreenerFilter('companies')
    const query = filter ? { filter } : null

    await router.push({
        path: `/companies/search`,
        query,
    })

    clearSearch()
}

const searchAllPatents = async () => {
    const currentRoute = router.currentRoute.value

    if (currentRoute.path === '/patent-search') {
        await router.replace({
            path: `/patent-search`,
            query: { seedString: searchTermTrimmed.value },
        })
    } else {
        await router.push({
            path: `/patent-search`,
            query: { seedString: searchTermTrimmed.value },
        })
    }

    clearSearch()
}

const setFocus = () => {
    if (searchInput.value) {
        setTimeout(() => {
            searchInput.value.setFocus()
        }, 100)
    }
}
</script>

<style lang="scss">
.global-search {
    &.alt-style {
        width: 900px !important;
    }

    .dropdown-results {
        top: 43px;
        left: 0;
        z-index: 2;

        &.alt-style {
            top: 78px !important;
        }

        &.add-border {
            border-top: solid var(--border-width-thin) var(--color-text-primary);
            border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
        }

        .totals {
            &:nth-of-type(1) {
                border-top: solid var(--border-width-thin) var(--color-border-default);
            }
        }
    }

    .result {
        transition: all ease-in-out 0.3s;
        cursor: pointer;
    }
}
</style>
